/**
 * Register Page Component
 * 
 * Provides user registration form with validation.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import { useI18n } from '../i18n';
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  areRequirementsMet,
  getPasswordRequirements,
  getUsernameRequirements
} from '../utils/authPolicy';
import './Auth.css';

const RequirementList = ({ requirements, label }) => (
  <ul className="requirement-list" aria-label={label}>
    {requirements.map((requirement) => (
      <li
        key={requirement.id}
        className={`requirement-item ${requirement.met ? 'met' : 'unmet'}`}
      >
        {requirement.label}
      </li>
    ))}
  </ul>
);

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const usernameRequirements = useMemo(
    () => getUsernameRequirements(formData.username, t),
    [formData.username, t]
  );
  const passwordRequirements = useMemo(
    () => getPasswordRequirements(formData.password, t),
    [formData.password, t]
  );
  const confirmPasswordRequirements = useMemo(
    () => [
      {
        id: 'passwords-match',
        label: t('auth.validation.passwordsMatch'),
        met:
          formData.confirmPassword.length > 0 &&
          formData.password === formData.confirmPassword
      }
    ],
    [formData.password, formData.confirmPassword, t]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error on input change
  };

  const validateForm = () => {
    const { email, password, confirmPassword } = formData;

    if (!areRequirementsMet(usernameRequirements)) {
      setError(t('auth.validation.usernameInvalid'));
      return false;
    }

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError(t('auth.validation.emailInvalid'));
      return false;
    }

    if (!areRequirementsMet(passwordRequirements)) {
      setError(t('auth.validation.passwordInvalid'));
      return false;
    }

    if (password !== confirmPassword) {
      setError(t('auth.validation.passwordMismatch'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await register(formData.username, formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || t('auth.register.failure'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <ThemeToggle className="auth-theme-toggle" />
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">⚡</span>
            <h1>{t('app.dashboardName')}</h1>
          </div>
          <p className="auth-subtitle">{t('auth.register.subtitle')}</p>
          {process.env.REACT_APP_DESKTOP === 'true' && (
            <p>Connect to the internet to create a spikescope.org account. The same account works on the website and in this app. After your first sign-in here, local analysis works offline.</p>
          )}
        </div>

        {error && (
          <div className="auth-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">{t('auth.register.usernameLabel')}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={t('auth.register.usernamePlaceholder')}
              required
              autoFocus
              autoComplete="username"
              minLength={USERNAME_MIN_LENGTH}
              maxLength={USERNAME_MAX_LENGTH}
            />
            <RequirementList
              requirements={usernameRequirements}
              label={t('auth.register.usernameRequirements')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.register.emailLabel')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('auth.register.emailPlaceholder')}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.register.passwordLabel')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('auth.register.passwordPlaceholder')}
              required
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              maxLength={PASSWORD_MAX_LENGTH}
            />
            <RequirementList
              requirements={passwordRequirements}
              label={t('auth.register.passwordRequirements')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.register.confirmPasswordLabel')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('auth.register.confirmPasswordPlaceholder')}
              required
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              maxLength={PASSWORD_MAX_LENGTH}
            />
            <RequirementList
              requirements={confirmPasswordRequirements}
              label={t('auth.register.confirmationRequirements')}
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="button-spinner"></span>
                {t('auth.register.submitting')}
              </>
            ) : (
              t('auth.register.submit')
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className="auth-link">
              {t('auth.register.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
